import { Table } from '@/components/Table';
import { CampaignCardActions } from '@/dashboards/CampaignPlanner/components/CampaignCard/CampaignCardActions';
import { formatCampaignTargetValue } from '@/dashboards/CampaignPlanner/helpers/formatCampaignTargetValue';
import { getCampaignFlightStatus } from '@/dashboards/CampaignPlanner/helpers/getCampaignFlightStatus';
import { OPTIMISATION_BASE_STYLES } from '@/dashboards/CampaignPlanner/helpers/optimisationBaseStyles';
import { useCampaignPlannerContext } from '@/dashboards/CampaignPlanner/hooks/useCampaignPlannerContext';
import { useCampaignPlannerUrl } from '@/dashboards/CampaignPlanner/hooks/useCampaignPlannerUrl';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { Campaign } from '@/dashboards/CampaignPlanner/types';
import { DateFormat, formatDate } from '@/utils/dateUtils';
import { dashboardRootRoute, toPath } from '@/utils/routes';
import { Typography } from '@analytical-alley/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { HiSparkles } from 'react-icons/hi';

interface Props {
    campaigns: Campaign[];
}

export const CampaignPlannerTable = ({ campaigns }: Props) => {
    const {
        params: { projectId },
    } = useCampaignPlannerUrl();
    const { dashboardId } = useSearch({ strict: false });
    const navigate = useNavigate();

    const {
        setRequestCampaign,
        setDeleteCandidate,
        setSaveAsCampaignReference,
        setShowSaveCampaignDialog,
    } = useCampaignPlannerContext();
    const { contributionFormatter } = useDashboardGlobalContext();

    const navigateToCampaign = async (campaign: Campaign) => {
        await navigate({
            to: toPath(dashboardRootRoute, '/campaign-optimization/$campaignId'),
            params: { projectId, campaignId: campaign.id },
            search: {
                dashboardId,
                optimizationBase: campaign.optimizationBase,
                budget: campaign.budget,
                startDate: formatDate(campaign.startDate, DateFormat.URL),
                endDate: formatDate(campaign.endDate, DateFormat.URL),
            },
        });
    };

    const columns = useMemo<ColumnDef<Campaign, any>[]>(
        () => [
            {
                accessorKey: 'name',
                header: 'Name',
                meta: {
                    header: { className: 'px-6 py-4' },
                    cell: { className: 'px-6 py-4' },
                },
                cell: ({ row }) => {
                    const style =
                        OPTIMISATION_BASE_STYLES[row.original.optimizationBase];
                    return (
                        <div className="flex items-center gap-2">
                            <div
                                className={`w-7 h-7 shrink-0 rounded-md bg-linear-to-br ${style.icon} flex items-center justify-center`}
                            >
                                <HiSparkles className="w-4 h-4 text-white" />
                            </div>
                            <Typography
                                variant="bodyS"
                                className="font-semibold"
                            >
                                {row.original.name}
                            </Typography>
                        </div>
                    );
                },
            },
            {
                id: 'optimizationBase',
                header: 'Type',
                meta: {
                    header: { className: 'px-6 py-4' },
                    cell: { className: 'px-6 py-4' },
                },
                cell: ({ row }) => {
                    const style =
                        OPTIMISATION_BASE_STYLES[row.original.optimizationBase];
                    return (
                        <span
                            className={`text-xs font-semibold px-2 py-1 rounded-full ${style.tag}`}
                        >
                            {style.label}
                        </span>
                    );
                },
            },
            {
                accessorKey: 'budget',
                header: 'Target',
                meta: {
                    header: { className: 'px-6 py-4' },
                    cell: { className: 'px-6 py-4' },
                },
                cell: ({ row }) => (
                    <Typography variant="bodyS">
                        {formatCampaignTargetValue(
                            row.original.optimizationBase,
                            row.original.budget,
                            contributionFormatter,
                        )}
                    </Typography>
                ),
            },
            {
                id: 'period',
                header: 'Period',
                meta: {
                    header: { className: 'px-6 py-4' },
                    cell: { className: 'px-6 py-4' },
                },
                cell: ({ row }) => (
                    <Typography variant="bodyS">
                        {formatDate(row.original.startDate, DateFormat.Normal)} -{' '}
                        {formatDate(row.original.endDate, DateFormat.Normal)}
                    </Typography>
                ),
            },
            {
                id: 'status',
                header: 'Status',
                meta: {
                    header: { className: 'px-6 py-4' },
                    cell: { className: 'px-6 py-4' },
                },
                cell: ({ row }) => {
                    const flightStatus = getCampaignFlightStatus(row.original);
                    return (
                        <span
                            className={`text-xs font-semibold px-2 py-1 rounded-full border ${flightStatus.color}`}
                        >
                            {flightStatus.label}
                        </span>
                    );
                },
            },
            {
                id: 'actions',
                header: '',
                meta: {
                    header: { className: 'px-6 py-4' },
                    cell: { className: 'px-6 py-4' },
                },
                cell: ({ row }) => {
                    const campaign = row.original;
                    const isDraft = campaign.status === 'draft';
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <CampaignCardActions
                                canRequestReview={isDraft}
                                canDelete={isDraft}
                                onSaveAs={() => {
                                    setSaveAsCampaignReference(campaign);
                                    setShowSaveCampaignDialog(true);
                                }}
                                onRequestReview={() =>
                                    setRequestCampaign(campaign)
                                }
                                onDelete={() => setDeleteCandidate(campaign.id)}
                            />
                        </div>
                    );
                },
            },
        ],
        [
            setRequestCampaign,
            setDeleteCandidate,
            setSaveAsCampaignReference,
            setShowSaveCampaignDialog,
            contributionFormatter,
        ],
    );

    return (
        <Table
            data={campaigns}
            columns={columns}
            onRowClick={navigateToCampaign}
        />
    );
};
